import {
  Component,
  DestroyRef,
  NgZone,
  TemplateRef,
  ViewChild,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  CEMENTO_CONTACT_NAME_MAX_LENGTH,
  CEMENTO_DELIVERY_INSTRUCTIONS_MAX_LENGTH,
  CementoCheckoutState,
  CementoFacturacionSelection,
} from 'src/app/models/cemento-checkout';
import { CementoCheckoutFlowService } from 'src/app/services/cemento-checkout-flow.service';
import { RutService } from 'src/app/services/rut.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ResponsiveLayoutService } from 'src/app/services/responsive-layout.service';
import { REGIONES_COMUNA_PAGO } from 'src/app/shared/utils/regiones';
import { resolveBillingAddressFromGeocoder } from 'src/app/shared/utils/billing-address-geocode.util';
type FacturacionForm = FormGroup<{
  email: FormControl<string>;
  name: FormControl<string>;
  rut: FormControl<string>;
  sameDispatchAddress: FormControl<boolean>;
  addressDisplay: FormControl<string>;
  address: FormControl<string>;
  contactName: FormControl<string>;
  contactPhone: FormControl<string>;
  deliveryInstructions: FormControl<string>;
  acceptTerms: FormControl<boolean>;
}>;

@Component({
  selector: 'app-cemento-facturacion-section',
  templateUrl: './cemento-facturacion-section.component.html',
  styleUrls: ['./cemento-facturacion-section.component.css'],
})
export class CementoFacturacionSectionComponent {
  private readonly fb = inject(FormBuilder);
  private readonly rutService = inject(RutService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly flow = inject(CementoCheckoutFlowService);
  private readonly modalService = inject(NgbModal);
  readonly responsiveService = inject(ResponsiveLayoutService);
  private readonly ngZone = inject(NgZone);

  private readonly checkoutState = toSignal(this.flow.checkoutState, {
    initialValue: {} as CementoCheckoutState,
  });

  readonly readOnly = input(false);

  readonly validChange = output<boolean>();
  readonly summaryDraftChange = output<Pick<CementoCheckoutState, 'facturacion'> | null>();

  readonly regionesComunas = REGIONES_COMUNA_PAGO;
  readonly deliveryInstructionsMaxLength = CEMENTO_DELIVERY_INSTRUCTIONS_MAX_LENGTH;
  readonly contactNameMaxLength = CEMENTO_CONTACT_NAME_MAX_LENGTH;

  readonly form: FacturacionForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    name: ['', [Validators.required]],
    rut: ['', [Validators.required]],
    sameDispatchAddress: [false],
    addressDisplay: [''],
    address: ['', [Validators.required]],
    contactName: [
      '',
      [Validators.required, Validators.maxLength(CEMENTO_CONTACT_NAME_MAX_LENGTH)],
    ],
    contactPhone: ['', [Validators.required, chileMobilePhoneValidator()]],
    deliveryInstructions: ['', [Validators.maxLength(CEMENTO_DELIVERY_INSTRUCTIONS_MAX_LENGTH)]],
    acceptTerms: [false, [Validators.requiredTrue]],
  });

  readonly formAddress = this.fb.nonNullable.group({
    street: ['', Validators.required],
    number: ['', Validators.required],
    comuna: ['', Validators.required],
    region: ['', Validators.required],
  });

  private readonly geocoder = new google.maps.Geocoder();

  @ViewChild('addressModal') addressModalTpl?: TemplateRef<unknown>;

  get comunaByRegion(): string[] {
    const region = this.formAddress.get('region')?.value;
    if (region) {
      return (
        REGIONES_COMUNA_PAGO.find((r) => r.nombreRegion === region)?.comunas ?? []
      );
    }
    return [];
  }

  constructor() {
    this.formAddress.controls.region.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.formAddress.controls.comuna.setValue('');
      });

    this.form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.validChange.emit(this.form.valid));

    this.form.controls.name.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((name) => this.emitSummaryDraftFromName(name));
    this.form.controls.sameDispatchAddress.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((checked) => {
        const dispatchAddress =
          this.checkoutState()?.mapa?.dispatchAddress?.address ?? '';
        this.applyDispatchAddressMode(checked, dispatchAddress);
      });
    effect(() => {
      const saved = this.checkoutState()?.facturacion;
      const dispatchAddress =
        this.checkoutState()?.mapa?.dispatchAddress?.address ?? '';
      if (!saved?.billing) {
        this.applyDispatchAddressMode(
          this.form.controls.sameDispatchAddress.value,
          dispatchAddress
        );
        if (this.readOnly()) {
          this.form.disable({ emitEvent: false });
          this.validChange.emit(this.isBillingRawValueComplete(this.form.getRawValue()));
        }
        return;
      }

      const shouldUseDispatchAddress = saved.sameDispatchAddress ?? false;
      const resolvedAddress = shouldUseDispatchAddress
        ? dispatchAddress
        : saved.billing.address ?? '';
      const nextValue = {
        email: saved.billing.email ?? '',
        name: saved.billing.name ?? '',
        rut: saved.billing.rut ?? '',
        sameDispatchAddress: shouldUseDispatchAddress,
        addressDisplay: resolvedAddress,
        address: resolvedAddress,
        contactName: saved.contactName ?? saved.billing.giro ?? '',
        contactPhone: saved.contactPhone ?? saved.billing.phone ?? '',
        deliveryInstructions: saved.deliveryInstructions ?? '',
        acceptTerms: saved.acceptTerms ?? false,
      };

      const currentValue = this.form.getRawValue();
      const isDifferent =
        currentValue.email !== nextValue.email ||
        currentValue.name !== nextValue.name ||
        currentValue.rut !== nextValue.rut ||
        currentValue.sameDispatchAddress !== nextValue.sameDispatchAddress ||
        currentValue.address !== nextValue.address ||
        currentValue.addressDisplay !== nextValue.addressDisplay ||
        currentValue.contactName !== nextValue.contactName ||
        currentValue.contactPhone !== nextValue.contactPhone ||
        currentValue.deliveryInstructions !== nextValue.deliveryInstructions ||
        currentValue.acceptTerms !== nextValue.acceptTerms;

      if (isDifferent) {
        this.form.patchValue(nextValue, { emitEvent: false });
        this.validChange.emit(this.form.valid);
        this.emitSummaryDraftFromName(nextValue.name);
      }
      this.applyDispatchAddressMode(
        this.form.controls.sameDispatchAddress.value,
        dispatchAddress
      );
      if (this.readOnly()) {
        this.form.disable({ emitEvent: false });
        this.validChange.emit(this.isBillingRawValueComplete(this.form.getRawValue()));
      }
    });
    this.validChange.emit(this.form.valid);
  }

  resetForm(): void {
    this.form.reset();
    this.validChange.emit(false);
    this.summaryDraftChange.emit(null);
  }

  getSelection(): CementoFacturacionSelection | null {
    const raw = this.form.getRawValue();
    if (this.readOnly()) {
      if (!this.isBillingRawValueComplete(raw)) {
        return null;
      }
      return this.mapRawToSelection(raw);
    }
    if (!this.form.valid) {
      return null;
    }
    return this.mapRawToSelection(raw);
  }

  private mapRawToSelection(raw: ReturnType<FacturacionForm['getRawValue']>): CementoFacturacionSelection {
    const {
      email,
      name,
      rut,
      address,
      contactName,
      contactPhone,
      deliveryInstructions,
      sameDispatchAddress,
      acceptTerms,
    } = raw;
    return {
      billing: {
        email,
        name,
        rut,
        giro: contactName,
        address,
        phone: contactPhone,
      },
      contactName,
      contactPhone,
      deliveryInstructions,
      sameDispatchAddress,
      acceptTerms,
    };
  }

  /** Formulario deshabilitado (cotización recuperada): valid es false aunque los datos sean correctos. */
  private isBillingRawValueComplete(
    raw: ReturnType<FacturacionForm['getRawValue']>
  ): boolean {
    const email = (raw.email ?? '').trim();
    const name = (raw.name ?? '').trim();
    const rut = (raw.rut ?? '').trim();
    const address = (raw.address ?? '').trim();
    const contactName = (raw.contactName ?? '').trim();
    const contactPhone = (raw.contactPhone ?? '').trim();
    return Boolean(
      email &&
        name &&
        rut &&
        address &&
        contactName &&
        contactPhone &&
        raw.acceptTerms
    );
  }

  checkRut(): void {
    const rutControl = this.form.controls.rut;
    const cleanValue = this.rutService.rutClean(rutControl.value ?? '');
    const valid = this.rutService.getRutChile(0, cleanValue);

    if (valid) {
      rutControl.setErrors(null);
      rutControl.setValue(this.rutService.rutFormat(cleanValue));
      return;
    }

    rutControl.setErrors({ invalidRut: true });
  }

  openAddressModal(): void {
    if (this.form.controls.sameDispatchAddress.value) {
      return;
    }
    const tpl = this.addressModalTpl;
    if (!tpl) {
      return;
    }
    this.modalService.open(tpl, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'lg',
    });
  }

  guardarAddress(): void {
    const formValue = this.formAddress.getRawValue();
    this.form.controls.sameDispatchAddress.setValue(false);
    const line = `${formValue.street} ${formValue.number}, ${formValue.comuna}, ${formValue.region}`;

    this.form.controls.addressDisplay.setValue('');
    this.form.controls.address.setValue('');
    this.form.controls.addressDisplay.setErrors(null);

    this.geocoder.geocode({ address: line }, (results, status) => {
      this.ngZone.run(() => {
        if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
          const outcome = resolveBillingAddressFromGeocoder(results[0], line);
          if (outcome.status === 'ok') {
            this.form.controls.addressDisplay.setValue(outcome.displayAddress);
            this.form.controls.address.setValue(outcome.billingAddress);
          } else {
            this.form.controls.addressDisplay.setErrors({ noValid: true });
          }
        } else {
          this.form.controls.addressDisplay.setErrors({ noValid: true });
        }
        this.modalService.dismissAll();
      });
    });
  }

  private emitSummaryDraftFromName(name: string): void {
    const trimmedName = (name ?? '').trim();
    this.summaryDraftChange.emit(
      trimmedName ? { facturacion: { billing: { name: trimmedName } } } : null
    );
  }

  contactPhoneShowError(): boolean {
    const control = this.form.controls.contactPhone;
    return control.touched && control.invalid;
  }

  private applyDispatchAddressMode(checked: boolean, dispatchAddress: string): void {
    const addressControl = this.form.controls.address;
    const displayControl = this.form.controls.addressDisplay;

    if (checked) {
      if (addressControl.value !== dispatchAddress) {
        addressControl.setValue(dispatchAddress, { emitEvent: false });
      }
      if (displayControl.value !== dispatchAddress) {
        displayControl.setValue(dispatchAddress, { emitEvent: false });
      }
      displayControl.setErrors(null);
      if (addressControl.enabled) {
        addressControl.disable({ emitEvent: false });
      }
      return;
    }

    if (addressControl.disabled) {
      addressControl.enable({ emitEvent: false });
    }
    displayControl.setErrors(null);
  }

  openModal(content: any) {
    this.modalService.open(content, { centered: true, backdrop: true, size: "lg" });
  }

}

/** Celular Chile: 9 dígitos locales comenzando en 9 (con o sin prefijo 56). */
function chileMobilePhoneValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const digits = (control.value ?? '').toString().replace(/\D/g, '');
    if (!digits.length) {
      return null;
    }
    const local = digits.startsWith('56') ? digits.slice(2) : digits;
    if (local.length === 9 && local.startsWith('9')) {
      return null;
    }
    return { invalidPhone: true };
  };
}
